<!-- Staff notification Email/Text grid — shared by PreferencesModal (the
     user-menu popup, where staff normally manage it) and the admin
     Settings > Notifications page. Every toggle saves immediately. See
     backend api/endpoints/admin_notification_prefs.py. -->
<template>
  <div>
    <div v-if="loading" class="text-sm text-fg-muted dark:text-fg-muted-dark py-8 text-center">Loading…</div>
    <div v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400 py-4">{{ loadError }}</div>
    <div v-else-if="!prefs.categories.length" class="text-sm text-fg-muted dark:text-fg-muted-dark py-4">
      There are no staff notifications for your account's role.
    </div>

    <div v-else class="space-y-4">
      <!-- Not opted in to texts: amber banner to the one-time consent page
           (the public /preferences/:token opt-in, the surface approved in our
           SMS campaign — never captured here). Once opted in there's no
           global switch: the per-row Text toggles are the control. Same
           pattern as the customer page (CommunicationPreferencesPage.vue). -->
      <template v-if="prefs.sms_enabled_for_tenant && !prefs.sms_consented">
        <div v-if="!prefs.phone" class="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          Text messages are turned off. Add a phone number under Edit Profile to opt in.
        </div>
        <button
          v-else
          type="button"
          :disabled="busy"
          @click="startOptIn"
          class="w-full text-left rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors disabled:opacity-50"
        >
          Text messages are turned off for your account. <span class="underline font-medium">Opt in to texts →</span>
          <span v-if="demoOptInNote" class="block text-xs mt-1">Demo mode: this opens the one-time SMS consent page.</span>
        </button>
      </template>

      <div class="bg-surface dark:bg-surface-dark rounded-lg border border-stroke dark:border-stroke-dark divide-y divide-stroke-subtle dark:divide-stroke-dark">
        <div class="flex items-center gap-4 px-4 py-2 text-[11px] uppercase tracking-wide font-medium text-fg-faint dark:text-fg-faint-dark">
          <span class="flex-1">Notification</span>
          <span class="w-12 text-center">Email</span>
          <span v-if="prefs.sms_enabled_for_tenant" class="w-12 text-center">Text</span>
        </div>
        <template v-for="(cat, i) in prefs.categories" :key="cat.key">
          <!-- Group header, only when this person has categories from more than
               one role (e.g. a teacher who also handles onboarding). -->
          <div
            v-if="multiGroup && cat.group !== prefs.categories[i - 1]?.group"
            class="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide font-semibold text-fg-muted dark:text-fg-muted-dark bg-surface-muted/50 dark:bg-surface-muted-dark/50"
          >{{ cat.group }}</div>
          <div class="flex items-center gap-4 px-4 py-3">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-fg-base dark:text-fg-base-dark flex items-center gap-2">
                {{ cat.label }}
                <span
                  v-if="cat.required"
                  class="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark"
                >Required</span>
              </div>
              <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ cat.description }}</p>
            </div>
            <div class="w-12 flex justify-center">
              <ToggleSwitch
                :model-value="cat.email"
                :disabled="busy || (cat.required && cat.email && !cat.sms)"
                :title="cat.required && cat.email && !cat.sms ? 'Turn on text for this first — required notifications need at least one channel.' : ''"
                @update:model-value="save(cat, { email: $event })"
              />
            </div>
            <div v-if="prefs.sms_enabled_for_tenant" class="w-12 flex justify-center">
              <ToggleSwitch
                :model-value="cat.sms"
                :disabled="busy || !prefs.sms_consented || (cat.required && cat.sms && !cat.email)"
                :title="!prefs.sms_consented ? 'Opt in to texts above first.' : (cat.required && cat.sms && !cat.email ? 'Turn on email for this first — required notifications need at least one channel.' : '')"
                @update:model-value="save(cat, { sms: $event })"
              />
            </div>
          </div>
        </template>
      </div>

      <div v-if="actionError" class="text-sm text-red-600 dark:text-red-400">{{ actionError }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useApi } from '../../composables/useApi'
import ToggleSwitch from './ToggleSwitch.vue'
import { useDemoMode } from '../../composables/useDemoMode'

const { fetchApi } = useApi()
// Demo Mode's fetch interceptor answers every write with {} and never
// persists — so in demo, apply changes to local state only (and never leave
// the page for the opt-in flow) instead of replacing prefs with that {}.
const { active: demoActive } = useDemoMode()
const demoOptInNote = ref(false)

const loading = ref(true)
const loadError = ref('')
const actionError = ref('')
const busy = ref(false)
const multiGroup = computed(() => new Set(prefs.value.categories.map(c => c.group)).size > 1)
const prefs = ref({ phone: null, sms_consented: false, sms_enabled_for_tenant: false, categories: [] })

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    prefs.value = await fetchApi('/api/admin/notification-prefs/me')
  } catch (e) {
    loadError.value = e.detail || 'Could not load notification preferences.'
  } finally {
    loading.value = false
  }
}

async function run(fn) {
  busy.value = true
  actionError.value = ''
  try {
    await fn()
  } catch (e) {
    actionError.value = e.detail || 'Something went wrong.'
  } finally {
    busy.value = false
  }
}

function save(cat, change) {
  if (demoActive.value) return Object.assign(cat, change)
  return run(async () => {
    prefs.value = await fetchApi('/api/admin/notification-prefs/me', {
      method: 'PUT',
      body: JSON.stringify({ category: cat.key, email: cat.email, sms: cat.sms, ...change }),
    })
  })
}

function startOptIn() {
  // Demo can't honestly simulate leaving for the consent page — just say so.
  if (demoActive.value) {
    demoOptInNote.value = true
    return
  }
  return run(async () => {
    const { url } = await fetchApi('/api/admin/notification-prefs/me/sms-opt-in-link', { method: 'POST' })
    window.location.href = url
  })
}

onMounted(load)
</script>
